/**
 * Formatting shared by every receipt renderer.
 *
 * One module rather than a copy per renderer, because the browser receipt and the thermal
 * receipt are the same document printed two ways. When the money, the dates or the payment
 * labels are written twice they drift, and the shop ends up with a Bluetooth receipt that
 * disagrees with the one the PC prints for the same sale.
 */

export const money = (value: number) => value.toFixed(2)

export const lira = (value: number) => Math.round(value).toLocaleString('en-US')

/**
 * How a payment method reads to a customer.
 *
 * The stored values are enum names — `CashUsd`, `OnAccount` — which are fine in a database and
 * wrong on a slip somebody is handed.
 */
export function methodLabel(method: string): string {
  switch (method) {
    case 'CashUsd':
      return 'Cash USD'
    case 'CashLbp':
      return 'Cash LBP'
    case 'OnAccount':
      return 'On account'
    case 'Card':
      return 'Card'
    case 'Voucher':
      return 'Voucher'
    default:
      return method
  }
}

/**
 * Date and time for the top of a receipt.
 *
 * Deliberately short and fixed rather than `toLocaleString()`, whose output length depends on
 * the device's locale. On 58mm paper there are 32 characters to a line and this shares one with
 * the receipt number, so a long-form date pushes the number off the paper on one cashier's
 * phone and not on another's.
 */
export function receiptDateTime(iso: string): string {
  const at = new Date(iso)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${at.getFullYear()} ${pad(at.getHours())}:${pad(at.getMinutes())}`
}
